// lib/sentry-base.ts
import { SENTRY_DSN } from "@/lib/constants";
import type { Integration } from "@sentry/core";
import Logger from '@/lib/logger';

const logger = new Logger('pbs:sentry:base:config');
logger.info("Initializing Sentry base config ...");

export interface SentryConfig {
  dsn: string;
  environment: string;
  release?: string;
  sampleRate: number;
  tracesSampleRate: number;
  replaysSessionSampleRate: number;
  replaysOnErrorSampleRate: number;
  skipOpenTelemetrySetup: boolean;
  debug: boolean;
  integrations: Integration[];
}

export const defaultInit: Omit<SentryConfig, 'environment'> = {
  dsn: SENTRY_DSN,
  sampleRate: 0.0,
  tracesSampleRate: 0,
  replaysSessionSampleRate: 0.0,
  replaysOnErrorSampleRate: 0.0,
  skipOpenTelemetrySetup: true,
  debug: false,
  integrations: [],
};

// A single container image is promoted from preprod through staging to production,
// so the environment cannot be baked in at build time and has to be resolved per
// runtime. On the server that is a plain environment variable. In the browser it is
// the data attribute the root layouts emit onto <html>, which is available before any
// bundle executes -- process.env is not, because Next.js only inlines NEXT_PUBLIC_*.
export function resolveEnvironment(): string | undefined {
  return typeof window === 'undefined'
    ? process.env.SENTRY_ENVIRONMENT
    : document.documentElement.dataset.sentryEnvironment;
}

export function createSentryConfig(overrides: Partial<SentryConfig> = {}): SentryConfig {
  const config: SentryConfig = {
    ...defaultInit,
    environment: "development",
    ...overrides
  };

  const environment = resolveEnvironment();

  switch (environment) {
    case "edcar":
      logger.debug("Overriding Sentry base config with edcar settings...");
      return {
        ...config,
        environment: "edcar",
        sampleRate: 0.5,
        tracesSampleRate: 0.5,
        replaysSessionSampleRate: 0.5,
        replaysOnErrorSampleRate: 1.0,
      };
    case "staging":
      logger.debug("Overriding Sentry base config with staging settings...");
      return {
        ...config,
        environment: "staging",
        sampleRate: 1.0,
        tracesSampleRate: 1.0,
        replaysSessionSampleRate: 1.0,
        replaysOnErrorSampleRate: 1.0,
      };
    case "preprod":
      logger.debug("Overriding Sentry base config with preprod settings...");
      return {
        ...config,
        environment: "preprod",
        sampleRate: 1.0,
        tracesSampleRate: 1.0,
        replaysSessionSampleRate: 1.0,
        replaysOnErrorSampleRate: 1.0,
      };
    case "production":
      logger.debug("Overriding Sentry base config with production settings...");
      return {
        ...config,
        environment: "production",
        // Only production names a release, so no other environment's events can cause
        // Sentry to auto-create one. The value is baked in by the image build.
        release: process.env.NEXT_PUBLIC_SENTRY_RELEASE,
        sampleRate: 0.25,
        tracesSampleRate: 1,
        replaysSessionSampleRate: 0.2,
        replaysOnErrorSampleRate: 1.0,
      };
    default:
      // An unresolved environment means two very different things. Locally there is no
      // SENTRY_ENVIRONMENT and no data attribute, and staying quiet is correct. In a built image
      // NODE_ENV is "production", so an unresolved environment is a misconfiguration: an
      // environment name with no case above, or a document that never emitted the attribute --
      // global-error.tsx renders its own <html> and cannot emit it, because a client component
      // only sees NEXT_PUBLIC_* variables. Reporting those as "unknown" at full error rate is
      // what keeps the event from being dropped by a sampleRate of 0, which in Sentry is
      // indistinguishable from no error having happened. Traces and session replays stay off:
      // the error is what matters here, and the volume is unbounded by definition.
      if (process.env.NODE_ENV === "production") {
        logger.error(
          `Could not resolve the Sentry environment (got ${JSON.stringify(environment)}); reporting as "unknown".`
        );
        return {
          ...config,
          environment: "unknown",
          sampleRate: 1.0,
          tracesSampleRate: 0,
          replaysSessionSampleRate: 0.0,
          replaysOnErrorSampleRate: 1.0,
        };
      }

      logger.debug("Accepting Sentry default base config without modification...");
      return {
        ...config,
        environment: "development",
      };
  }
}
